import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';
import { ContentDto } from '@cineamo/api/models/content/ContentDto.types';
import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { getCinemaContentUpcomings } from '@cineamo/api/resources/cinema-content-upcoming/cinema-content-upcoming-api';
import { CinemaContentUpcomingScopeEnum } from '@cineamo/api/resources/cinema-content-upcoming/CinemaContentUpcomingScopeEnum.types';
import { getCinemaFeaturedContents } from '@cineamo/api/resources/cinema-featured-content/cinema-featured-content-api';
import { ShowingApiGroup } from '@cineamo/api/resources/showing/showing-api.group';

import { CachedContentData, getCachedContentData } from '@/hooks/useCachedContentData';
import { getFutureShowingsFiltered, applyClientFilters } from '@/hooks/useFutureShowings';

import { formatDate_yyyyMMddTHHmmssZ } from '@/utils/date-helper';

import { programDayEnd, programDayKeyFromToday, programDayStart, startOfProgramToday } from './program-day';

function getFirstShowingForContents(allShowings: ShowingDto[]): ShowingDto[] {
    const resultMap = new Map<ContentDto['id'], ShowingDto>();
    allShowings.forEach((showing) => {
        const previousShowing = resultMap.get(showing.contentId);
        if (previousShowing) {
            if (new Date(previousShowing.startDatetime).getTime() > new Date(showing.startDatetime).getTime()) {
                resultMap.set(showing.contentId, showing);
            }
        } else {
            resultMap.set(showing.contentId, showing);
        }
    });
    return Array.from(resultMap.values());
}

export const getMoviesInCinema = async (
    allShowings: ShowingDto[],
    cinema: CinemaDto,
    locale: string,
    timeZone: string
) => {
    const firstShowings = getFirstShowingForContents(allShowings);
    return Promise.all(
        (
            await applyClientFilters(
                firstShowings,
                {
                    startDatetime: formatDate_yyyyMMddTHHmmssZ(startOfProgramToday(timeZone)),
                    group: ShowingApiGroup.Content,
                    sort: 'popularity',
                    order: 'desc'
                },
                { locale, cinemaId: cinema.id }
            )
        )
            .map((showing) => showing._embedded?.content)
            .filter((content): content is ContentDto => !!content)
            .map((content) => getCachedContentData({ content, cinema, locale }))
    );
};

export const getMoviesComingSoon = async (cinemaId: number, cinema: CinemaDto, locale: string, page: number = 1) => {
    const response = await getCinemaContentUpcomings(cinemaId, {
        // CIN-14687: the API decides what still counts as "soon" — a cinema release date not reached
        // yet, or no release date and nothing scheduled — and reads the date in the cinema's own
        // timezone. A `releaseDateStart` sent from here could only ever be the render server's idea of
        // today, and `>= today` kept an entry up on the very day the film opened.
        scope: [CinemaContentUpcomingScopeEnum.NOT_RELEASED],
        sort: 'position',
        order: 'asc',
        page: page
    });
    const contents = await Promise.all(
        (response?._embedded?.['cinema-content-upcomings'] ?? [])
            .map((cinemaContentUpcoming) => ({
                content: cinemaContentUpcoming._embedded?.content,
                releaseDate: cinemaContentUpcoming.releaseDate
            }))
            .filter((upcoming): upcoming is typeof upcoming & { content: ContentDto } => !!upcoming.content)
            .map((upcoming) =>
                getCachedContentData({
                    content: upcoming.content,
                    cinema,
                    locale,
                    overrideReleaseDate: upcoming.releaseDate ? upcoming.releaseDate : false
                })
            )
    );
    return { ...response, _embedded: { 'cinema-content-upcomings': contents } };
};

export const HighlightsStartDatetime = formatDate_yyyyMMddTHHmmssZ(new Date());
export const getHighlights = async (cinemaId: number) =>
    await getFutureShowingsFiltered(cinemaId, {
        isHighlightAfterDatetime: HighlightsStartDatetime
    });

export type FeaturedContentData = CachedContentData & {
    highlightText: string | null;
    showFeatureBadge: boolean;
    featureBadge: string | null;
    showButton: boolean;
    buttonTitle: string | null;
    highlightColor: string | null;
    link: string | null;
    reason: string | null;
};

export const getPremieres = async (allShowings: ShowingDto[], cinema: CinemaDto, locale: string, timeZone: string) => {
    const premieresStartDatetime = formatDate_yyyyMMddTHHmmssZ(startOfProgramToday(timeZone));
    const premieresEndDatetime = formatDate_yyyyMMddTHHmmssZ(
        programDayEnd(programDayKeyFromToday(180, timeZone), timeZone)
    );

    return Promise.all(
        (
            await applyClientFilters(
                allShowings,
                {
                    group: ShowingApiGroup.Content,
                    hasMovieId: true,
                    sort: 'startDatetime',
                    order: 'asc'
                },
                { locale, cinemaId: cinema.id }
            )
        )
            .map((showing) => showing._embedded?.content)
            .filter((content): content is ContentDto => !!content)
            .map((content) => getCachedContentData({ content, cinema, locale }))
    ).then((contents) =>
        contents.filter((content) => {
            const startDate = new Date(premieresStartDatetime).getTime();
            const endDate = new Date(premieresEndDatetime).getTime();
            if (!content?.releaseDate) {
                return false;
            }
            const releaseDate = programDayStart(content.releaseDate, timeZone).getTime();
            if (releaseDate < startDate || releaseDate >= endDate) {
                return false;
            }

            return allShowings.some((showing) => {
                if (showing.contentId !== content.id) {
                    return false;
                }
                return programDayStart(showing.startDatetime, timeZone).getTime() <= releaseDate;
            });
        })
    );
};

export const getFeaturedMovies = async (
    cinemaId: number,
    cinema: CinemaDto,
    locale: string
): Promise<FeaturedContentData[]> => {
    const response = await getCinemaFeaturedContents(cinemaId, {
        activeDatetime: formatDate_yyyyMMddTHHmmssZ(new Date()),
        sort: 'position',
        order: 'asc'
    });
    const featuredContents = response?._embedded?.['cinema-featured-contents'] ?? [];
    return Promise.all(
        featuredContents
            .filter((item): item is typeof item & { _embedded: { content: ContentDto } } => !!item._embedded?.content)
            .map(async (item) => {
                const contentData = await getCachedContentData({
                    content: item._embedded.content,
                    cinema,
                    locale
                });
                return {
                    ...contentData,
                    highlightText: item.highlightText ?? null,
                    showFeatureBadge: item.showFeatureBadge ?? false,
                    featureBadge: item.featureBadge ?? null,
                    showButton: item.showButton ?? false,
                    buttonTitle: item.buttonTitle ?? null,
                    highlightColor: item.highlightColor ?? null,
                    link: item.link ?? null,
                    reason: item.reason ?? null
                };
            })
    );
};
